import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { useSyncContext } from '../../contexts/SyncContext';
import InterviewCalendar from '../../components/InterviewCalendar';
import InterviewScheduler from '../../components/InterviewScheduler';
import CandidateList from './CandidateList';
import aiAutomationApi from '../../utils/aiAutomationApi';
import { getMetadata } from '../../utils/encryptedStorage';
import { handleMailProviderReconnect } from '../../utils/gmailOAuth';
import { OverviewSkeleton } from '../../components/SkeletonLoader';
import { canUseCompanyFeature } from '../../utils/companyFeatureAccess';

const OverviewSection = ({ jobTitle, sessionId: propSessionId, onCandidateClick, scrollToCandidatesToken = 0, activeTab, onSidebarCollapse, onCandidateUpdate, onBack, featureAccess = null }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const urlSessionId = searchParams.get('session_id') || searchParams.get('id');
  const urlSearchId = searchParams.get('searchId') || '';
  const urlCandidateEmail = searchParams.get('candidate') || '';
  const urlScheduleModal = searchParams.get('schedule_modal') || '';
  const sessionId = propSessionId || urlSessionId;
  const appMetadata = getMetadata();
  const companyId = appMetadata?.cid || '';
  const currentUserId = appMetadata?.uid || appMetadata?.userId || appMetadata?.id || '';

  const [overview, setOverview] = useState(null);
  const [calendarData, setCalendarData] = useState({ interviews: [] });
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const { getSyncState, startSync, setCurrentSearchId } = useSyncContext();
  const syncState = getSyncState(overview?.search_id);
  const syncing = syncState.syncing;
  const syncMessage = syncState.syncMessage;
  const [schedulerCandidates, setSchedulerCandidates] = useState([]);
  const candidateListSectionRef = useRef(null);

  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';
  const canUseFeature = (feature) => canUseCompanyFeature(featureAccess, feature);
  const canUseInterviews = canUseFeature('manual_interview') || canUseFeature('ai_interview');
  const showEmailSync = canUseFeature('outreach') || canUseFeature('scheduling');

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      setOverview(getFallbackOverview());
      return;
    }

    let cancelled = false;

    const fetchOverview = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await aiAutomationApi.get(`/sessions/${sessionId}/overview`);
        if (!cancelled) setOverview(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setOverview(getFallbackOverview());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOverview();
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !canUseInterviews) {
      setCalendarData({ interviews: [] });
      return;
    }

    let cancelled = false;

    const fetchCalendar = async () => {
      try {
        const effectiveSearchId = overview?.search_id || urlSearchId;
        const query = new URLSearchParams();
        if (companyId) query.set('cid', String(companyId));
        if (currentUserId) query.set('user_id', String(currentUserId));
        if (effectiveSearchId) query.set('search_id', String(effectiveSearchId));
        if (urlCandidateEmail) query.set('candidate_email', String(urlCandidateEmail));
        const data = await aiAutomationApi.get(`/interviews/calendar/all${query.toString() ? `?${query.toString()}` : ''}`);
        if (!cancelled && data?.interviews) {
          setCalendarData({ interviews: data.interviews });
        }
      } catch {
        if (!cancelled) setCalendarData({ interviews: [] });
      }
    };

    fetchCalendar();
    return () => { cancelled = true; };
  }, [sessionId, overview?.search_id, urlSearchId, urlCandidateEmail, companyId, currentUserId, canUseInterviews]);

  const getFallbackOverview = () => ({
    hiring_overview: { title: 'Hiring Overview', updated_label: 'Not synced yet' },
    hiring_funnel: [
      { stage: 'Sourced', count: 0 },
      { stage: 'Contacted', count: 0 },
      { stage: 'Interested', count: 0 },
      { stage: 'Shortlisted', count: 0 },
      { stage: 'Interviewed', count: 0 },
    ],
    channel_performance: {
      total: 0,
      channels: [
        { name: 'LinkedIn', count: 0, color: '#0A66C2' },
        { name: 'Email', count: 0, color: '#EA4335' },
        { name: 'Naukri', count: 0, color: '#22C55E' },
        { name: 'Careers Page', count: 0, color: '#8B5CF6' },
        { name: 'Google Jobs', count: 0, color: '#FBBC05' },
      ],
    },
    responses_over_time: {
      title: 'Responses Over Time (Last 48 h)',
      data: Array.from({ length: 8 }, (_, i) => ({ label: `${(i + 1) * 6}h`, count: 0 })),
    },
  });

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleInitialMode, setScheduleInitialMode] = useState('');
  const [hiringOverviewExpanded, setHiringOverviewExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Bumped by the Refresh button → passed to CandidateList so it drops its cache and silently re-pulls.
  const [candidateRefreshToken, setCandidateRefreshToken] = useState(0);

  // Fetch candidates ready for scheduling via dedicated backend endpoint
  // that uses the same bucket logic as the lifecycle agent.
  useEffect(() => {
    if (!sessionId || !canUseInterviews) {
      setSchedulerCandidates([]);
      return;
    }
    let cancelled = false;
    const fetchCandidates = async () => {
      try {
        const data = await aiAutomationApi.get(`/sessions/${sessionId}/schedulable-candidates`);
        if (cancelled) return;
        const candidates = data?.candidates || [];
        console.log('[OverviewSection] Schedulable candidates (from backend):', candidates.length, candidates.map(c => ({
          name: c.name, email: c.email, doc_id: c.doc_id, cv_filename: c.cv_filename, funnel_stage: c.funnel_stage
        })));
        setSchedulerCandidates(candidates);
      } catch (err) {
        console.error('[OverviewSection] Failed to fetch schedulable candidates, falling back to session data:', err);
        // Fallback: fetch from session data and filter client-side
        try {
          const data = await aiAutomationApi.get(`/sessions/${sessionId}`);
          if (cancelled || !data?.searches?.length) return;
          const allCandidates = (data.searches || []).flatMap(
            (search) => search?.candidates || []
          );
          const schedulable = allCandidates.filter((c) => {
            const email = (c.email || c.candidate_email || '').trim();
            const hasResume = !!(c.doc_id || c.cv_filename);
            return !!email && hasResume;
          });
          console.log('[OverviewSection] Schedulable candidates (fallback):', schedulable.length);
          if (!cancelled) setSchedulerCandidates(schedulable);
        } catch {
          // ignore
        }
      }
    };
    fetchCandidates();
    return () => { cancelled = true; };
  }, [sessionId, canUseInterviews]);

  const handleScheduleInterview = () => {
    if (!canUseInterviews) return;
    setScheduleInitialMode('');
    setScheduleModalOpen(true);
  };

  useEffect(() => {
    if (!sessionId || !canUseInterviews) return;
    const key = `return-to-schedule-modal:${sessionId}`;
    const routeMode = ['manual', 'ai'].includes(urlScheduleModal) ? urlScheduleModal : '';
    const raw = window.sessionStorage.getItem(key);
    if (!raw && !routeMode) return;
    window.sessionStorage.removeItem(key);
    let storedMode = '';
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        storedMode = parsed?.mode || '';
      } catch {
        storedMode = '';
      }
    }
    setScheduleInitialMode(routeMode || storedMode);
    if (routeMode) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('schedule_modal');
      const nextSearch = nextParams.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
    }
    setScheduleModalOpen(true);
  }, [sessionId, canUseInterviews, urlScheduleModal, searchParams, navigate, location.pathname]);

  const refreshCalendar = useCallback(() => {
    if (!canUseInterviews) return;
    const effectiveSearchId = overview?.search_id || urlSearchId;
    const query = new URLSearchParams();
    if (companyId) query.set('cid', String(companyId));
    if (currentUserId) query.set('user_id', String(currentUserId));
    if (effectiveSearchId) query.set('search_id', String(effectiveSearchId));
    if (urlCandidateEmail) query.set('candidate_email', String(urlCandidateEmail));
    aiAutomationApi.get(`/interviews/calendar/all${query.toString() ? `?${query.toString()}` : ''}`)
      .then((data) => data?.interviews && setCalendarData({ interviews: data.interviews }))
      .catch(() => {});
  }, [overview?.search_id, urlSearchId, urlCandidateEmail, companyId, currentUserId, canUseInterviews]);

  // Silently refresh the whole overview surface — overview stats, schedulable candidates, the interview
  // calendar, the candidate table (cache-busted), and any open candidate drawer. This pulls freshly
  // changed data (e.g. an interview that just completed → its "Interview Report" button) WITHOUT the
  // user having to navigate to another page and back, which was previously the only way to force a
  // remount + refetch. No skeletons: current content stays on screen and updates in place.
  const handleRefreshOverview = useCallback(async () => {
    if (refreshing || !sessionId) return;
    setRefreshing(true);
    try {
      const [ovData, schedData] = await Promise.all([
        aiAutomationApi.get(`/sessions/${sessionId}/overview`).catch(() => null),
        canUseInterviews
          ? aiAutomationApi.get(`/sessions/${sessionId}/schedulable-candidates`).catch(() => null)
          : Promise.resolve(null),
      ]);
      if (ovData) setOverview(ovData);
      if (schedData?.candidates) setSchedulerCandidates(schedData.candidates);
      refreshCalendar();
      // Drop the candidate table's page cache and silently re-pull the current page.
      setCandidateRefreshToken((t) => t + 1);
      // Nudge any open candidate drawer to re-pull its booked-interview + funnel data (the unguarded
      // event path), so a just-completed interview surfaces its report button without a full remount.
      window.dispatchEvent(new CustomEvent('candidateDetailRefresh'));
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, sessionId, refreshCalendar, canUseInterviews]);

  // Auto-refresh when the recruiter returns to this tab/window. A candidate's interview finishes in a
  // separate flow the overview never observes, so coming back to this tab silently re-pulls the full
  // surface — the report button AND every other field that may have changed meanwhile (funnel stage,
  // candidate statuses, calendar, open drawer). Throttled so rapid tab-flipping doesn't spam the API,
  // and armed only while Overview is the active view.
  const lastFocusRefreshRef = useRef(0);
  useEffect(() => {
    if (activeTab !== 'overview' || !sessionId) return undefined;
    const maybeRefresh = () => {
      if (document.visibilityState === 'hidden') return; // ignore the "tab going away" transition
      const now = Date.now();
      if (now - lastFocusRefreshRef.current < 8000) return; // at most once per 8s
      lastFocusRefreshRef.current = now;
      handleRefreshOverview();
    };
    window.addEventListener('focus', maybeRefresh);
    document.addEventListener('visibilitychange', maybeRefresh);
    return () => {
      window.removeEventListener('focus', maybeRefresh);
      document.removeEventListener('visibilitychange', maybeRefresh);
    };
  }, [activeTab, sessionId, handleRefreshOverview]);

  useEffect(() => {
    if (!scrollToCandidatesToken) return;
    const node = candidateListSectionRef.current;
    if (!node) return;

    const timeout = setTimeout(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);

    return () => clearTimeout(timeout);
  }, [scrollToCandidatesToken]);

  // Refresh overview when sync completes from any source (lifecycle chat, button, etc.)
  useEffect(() => {
    const handler = () => {
      if (sessionId) {
        aiAutomationApi.get(`/sessions/${sessionId}/overview`)
          .then((data) => data && setOverview(data))
          .catch(() => {});
        // Also refresh schedulable candidates
        aiAutomationApi.get(`/sessions/${sessionId}/schedulable-candidates`)
          .then((data) => data?.candidates && setSchedulerCandidates(data.candidates))
          .catch(() => {});
      }
    };
    window.addEventListener('syncCompleted', handler);
    return () => window.removeEventListener('syncCompleted', handler);
  }, [sessionId]);

  // ── Sync emails (uses global SyncContext for persistence across routes) ──
  const handleSyncEmails = useCallback(async () => {
    if (!showEmailSync) return;
    if (syncing) return;
    if (!overview?.search_id) {
      console.warn('[Sync] No search_id available in overview');
      return;
    }
    setCurrentSearchId(overview.search_id);
    const metadata = getMetadata();
    startSync(overview.search_id, {
      sessionId,
      cid: metadata?.cid ? String(metadata.cid) : undefined,
      onComplete: async () => {
        try {
          const data = await aiAutomationApi.get(`/sessions/${sessionId}/overview`);
          if (data) setOverview(data);
        } catch { /* ignore */ }
      },
      onGmailNotConnected: async (authError) => {
        // Auto-trigger active mail provider OAuth popup — no error text beside button
        console.log('[OverviewSection] Mail provider not connected — triggering OAuth popup');
        try {
          await handleMailProviderReconnect({
            cid: metadata?.cid ? String(metadata.cid) : undefined,
            error: authError,
            onConnected: () => {
              // Refresh overview after mail provider connected
              aiAutomationApi.get(`/sessions/${sessionId}/overview`)
                .then((data) => data && setOverview(data))
                .catch(() => {});
            },
          });
        } catch { /* popup blocked or error — user will see the Credentials page instruction */ }
      },
    });
  }, [showEmailSync, syncing, overview?.search_id, sessionId, setCurrentSearchId, startSync]);

  const ov = overview || getFallbackOverview();
  const funnel = ov.hiring_funnel || [];
  const channels = ov.channel_performance?.channels || [];
  const totalResponses = ov.channel_performance?.total ?? 0;
  const responsesData = ov.responses_over_time?.data || [];
  const maxResponses = Math.max(1, ...responsesData.map((r) => r.count));

  if (loading && !overview) {
    return (
      <div className="flex-1 min-h-0 bg-white dark:bg-gray-900">
        <OverviewSkeleton />
      </div>
    );
  }

  /* Figma: Hiring Funnel horizontal stacked bar colors */
  const funnelColors = [primaryColor, secondaryColor, '#86EFAC', '#F59E0B', '#FDE047'];
  const funnelMax = Math.max(1, ...funnel.map((f) => f.count));

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950 min-h-0 w-full">
      <div className="px-4 sm:px-6 py-6 space-y-6 w-full">
        {/* Hiring Overview header - collapsible, same row, both ends, gradient text with rolling arrow */}
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => setHiringOverviewExpanded(!hiringOverviewExpanded)}
            className="flex min-w-0 flex-none items-center justify-start gap-2 px-0 py-1 text-left transition-opacity hover:opacity-80 sm:p-0"
          >
            <h2 className="text-base font-semibold leading-tight text-gray-900 dark:text-white pointer-events-none sm:text-lg sm:leading-normal">
              <span className="block sm:inline">Hiring</span>
              <span className="block sm:inline sm:ml-1">Overview</span>
            </h2>
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center text-gray-600 dark:text-gray-400 sm:h-auto sm:w-auto">
              <svg
                className={`w-4 h-4 transition-transform pointer-events-none sm:w-5 sm:h-5 ${hiringOverviewExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
          <div className="flex flex-shrink-0 items-center gap-3">
            {/* Updated label */}
            <span className="text-xs font-medium flex items-center gap-1.5">
              <svg 
                className="w-3.5 h-3.5" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <defs>
                  <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={primaryColor} />
                    <stop offset="100%" stopColor={secondaryColor} />
                  </linearGradient>
                </defs>
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  stroke="url(#arrowGradient)"
                />
              </svg>
              <span
                style={{
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {ov.hiring_overview?.updated_label || 'Not synced yet'}
              </span>
            </span>

            {/* Sync button */}
            {showEmailSync && (
              <button
                onClick={handleSyncEmails}
                disabled={syncing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all hover:shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  borderColor: `${primaryColor}40`,
                  color: primaryColor,
                  backgroundColor: 'transparent',
                }}
                title="Mail Sync Based on Subject"
              >
                <svg
                  className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    stroke="currentColor"
                  />
                </svg>
                {syncing ? 'Syncing…' : 'Mail Sync'}
              </button>
            )}
            {/* Refresh button — silently re-pulls all overview data (stats, candidates, calendar, open
                drawer) so freshly-changed records like a completed interview's report show without
                navigating away. Distinct from Sync, which pulls new emails from Gmail. */}
            <button
              onClick={handleRefreshOverview}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all hover:shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                borderColor: `${primaryColor}40`,
                color: primaryColor,
                backgroundColor: 'transparent',
              }}
              title="Refresh overview data"
            >
              <svg
                className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  stroke="currentColor"
                />
              </svg>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            
            {/* Sync status message */}
            {syncMessage && (
              <span className={`text-xs font-medium ${syncMessage.includes('fail') || syncMessage.includes('not') ? 'text-red-500' : 'text-green-500'}`}>
                {syncMessage}
              </span>
            )}
          </div>
        </div>

        {/* Three metrics - cards with shadows, no borders, larger size */}
        {hiringOverviewExpanded && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          {/* 1. Hiring Funnel */}
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/30 transition-colors duration-250">
            <h3 className="font-medium text-gray-900 dark:text-white mb-5" style={{ fontSize: '16px' }}>
              Hiring Funnel
            </h3>
            <div className="space-y-3 mb-3">
              {funnel.map((item, idx) => (
                <div key={item.stage} className="flex items-center gap-3">
                  <span className="w-24 text-sm text-gray-600 dark:text-gray-400">{item.stage}</span>
                  <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden max-w-[220px]">
                    <div
                      className="h-full rounded transition-all duration-300"
                      style={{
                        width: `${Math.min(100, (item.count / funnelMax) * 100)}%`,
                        backgroundColor: funnelColors[idx % funnelColors.length],
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white w-8">{item.count}</span>
                </div>
              ))}
            </div>
            {(() => {
              // Generate clean scale ticks (no duplicates)
              const ticks = [0];
              if (funnelMax <= 5) {
                for (let i = 1; i <= funnelMax; i++) ticks.push(i);
              } else {
                const step = Math.ceil(funnelMax / 4);
                for (let i = step; i < funnelMax; i += step) ticks.push(i);
                ticks.push(funnelMax);
              }
              return (
                <div className="flex items-center gap-3">
                  <span className="w-24 flex-shrink-0" />
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 px-1 max-w-[220px] flex-1">
                    {ticks.map((t) => <span key={t}>{t}</span>)}
                  </div>
                  <span className="w-8 flex-shrink-0" />
                </div>
              );
            })()}
          </div>

          {/* 2. Channel Performance */}
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/30 transition-colors duration-250">
            <h3 className="font-medium text-gray-900 dark:text-white mb-5" style={{ fontSize: '16px' }}>
              Channel Performance
            </h3>
            <div className="flex flex-col items-center">
              <div className="relative w-32 h-32 flex-shrink-0 mb-5">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  {(() => {
                    const total = channels.reduce((s, c) => s + c.count, 0) || 1;
                    const hasData = total > 0;
                    if (!hasData) {
                      return (
                        <circle
                          cx="18"
                          cy="18"
                          r="16"
                          fill="none"
                          stroke="#E5E7EB"
                          strokeWidth="3"
                          className="dark:stroke-gray-600"
                        />
                      );
                    }
                    const circumference = 2 * Math.PI * 16;
                    let offset = 0;
                    return channels
                      .filter((ch) => ch.count > 0)
                      .map((ch) => {
                        const pct = (ch.count / total) * 100;
                        const dash = (pct / 100) * circumference;
                        const strokeDasharray = `${dash} ${circumference}`;
                        const strokeDashoffset = -offset;
                        offset += dash;
                        return (
                          <circle
                            key={ch.name}
                            cx="18"
                            cy="18"
                            r="16"
                            fill="none"
                            stroke={ch.color}
                            strokeWidth="3"
                            strokeDasharray={strokeDasharray}
                            strokeDashoffset={strokeDashoffset}
                            className="transition-all duration-300"
                          />
                        );
                      });
                  })()}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">Total Candidates</span>
                  <span className="text-base font-semibold text-gray-900 dark:text-white">{totalResponses}</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
                {channels.filter((c) => c.count > 0).map((ch) => (
                  <div key={ch.name} className="flex items-center gap-1.5 text-xs">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: ch.color }}
                    />
                    <span className="text-gray-700 dark:text-gray-300">{ch.name}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{ch.count}</span>
                  </div>
                ))}
                {channels.filter((c) => c.count > 0).length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">No channel data yet</p>
                )}
              </div>
            </div>
          </div>

          {/* 3. Responses Over Time */}
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/30 transition-colors duration-250">
            <h3 className="font-medium text-gray-900 dark:text-white mb-5" style={{ fontSize: '16px' }}>
              {ov.responses_over_time?.title || 'Responses Over Time (Last 48 H)'}
            </h3>
            <div className="h-36 relative">
              <svg viewBox="0 0 200 80" preserveAspectRatio="none" className="w-full h-full">
                <defs>
                  <linearGradient id="areaGradient" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor={secondaryColor} stopOpacity="0.3" />
                    <stop offset="100%" stopColor={secondaryColor} stopOpacity="0" />
                  </linearGradient>
                </defs>
                {(() => {
                  const w = 200;
                  const h = 60;
                  const n = responsesData.length || 1;
                  const pts = responsesData.map((d, i) => {
                    const x = (n > 1 ? i / (n - 1) : 0.5) * w;
                    const y = h - (d.count / maxResponses) * h;
                    return `${x},${Math.max(0, Math.min(h, y))}`;
                  });
                  if (pts.length === 0) return null;
                  const areaPath = `M0,${h} L${pts.join(' L')} L${w},${h} Z`;
                  const linePath = `M${pts.join(' L')}`;
                  return (
                    <>
                      <path d={areaPath} fill="url(#areaGradient)" />
                      <path
                        d={linePath}
                        fill="none"
                        stroke={secondaryColor}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </>
                  );
                })()}
              </svg>
              <span className="absolute bottom-0 left-0 text-[10px] text-gray-500 dark:text-gray-400">
                Daily Responses
              </span>
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-gray-500 dark:text-gray-400 px-0.5">
              {responsesData.length > 0 && responsesData.map((d, i) => (
                <span key={i}>{d.label}</span>
              ))}
            </div>
          </div>
        </div>

        )}

        {canUseInterviews && (
          <>
            {/* Interview Calendar - merged with background, divider only */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <InterviewCalendar
                interviews={calendarData.interviews}
                searchId={overview?.search_id || urlSearchId}
                onScheduleInterview={handleScheduleInterview}
                defaultExpanded={false}
              />
            </div>

            <InterviewScheduler
              isOpen={scheduleModalOpen}
              onClose={() => setScheduleModalOpen(false)}
              searchId={overview?.search_id || urlSearchId}
              candidates={schedulerCandidates}
              onScheduleComplete={refreshCalendar}
              jobTitle={jobTitle}
              enableInterviewTypeChoice
              companyId={companyId}
              sourceSessionId={sessionId}
              featureAccess={featureAccess}
              alwaysShowInterviewTypeChoice
              initialInterviewMode={scheduleInitialMode}
            />
          </>
        )}

        {/* Candidate List — replaces former placeholder */}
        <div ref={candidateListSectionRef} className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <CandidateList
            searchId={overview?.search_id}
            sessionId={sessionId}
            onCandidateClick={onCandidateClick}
            activeTab={activeTab}
            jobTitle={jobTitle}
            onSidebarCollapse={onSidebarCollapse}
            onCandidateUpdate={onCandidateUpdate}
            onBack={onBack}
            refreshToken={candidateRefreshToken}
            featureAccess={featureAccess}
          />
        </div>
      </div>
    </div>
  );
};

export default OverviewSection;
